import React, { useContext, useEffect, useMemo, useState } from "react";
import { StyledDatagrid } from "../../../../../../styles/DatagridStyles";
import {
  GridColDef,
  GridValidRowModel,
  GridAlignment,
} from "@mui/x-data-grid-pro";
import { TableHeaderProps } from "../../../../../../utils/types";
import useAssetTableJson from "../../../../../../json/useAssetTableJson";
import Assign from "../../../../../../images/Assign.svg";
import { TABLE_ROW_HEIGHT } from "../../../../../../styles/Variables";
import { Button } from "../../../../../../stories/Button/Button";
import { Title } from "../../../../../../stories/Title/Title";
import Close from "../../../../../../images/Close.svg";
import {
  AtsRfqMstEdge,
  AtsRfqMstOrderField,
  GetRfqsResult,
  GetRfqsVars,
  RfqQueryType,
} from "../../quotation/queries/types";
import { useLazyQuery } from "@apollo/client";
import { GetRfqs } from "../../quotation/queries/queries";

import useUrlParams from "../../../../../../customHooks/useUrlParams";
import { AppContext } from "../../../../../../context/context";
import {
  EMPTY_STRING,
  FETCH_MORE_DATA,
  ROWS_PER_PAGE,
} from "../../../../../../utils/constants";
import {
  getModifiedScrollHeight,
  toIsoDate,
  toStandardDate,
} from "../../../../../../utils/UtilFunctions";
import { OrderDirection } from "../../../../../../utils/commonQueryTypes";
import { formDates } from "../../material/ApprovedList";

const Data = [
  {
    mrf_no: 1,
    mrf_date: "10-10-2023",
    subsidiary: "Demo",
    total_items: 0,
    total_quantity: 0,
    approx_netvalue: 0,
    acceptance_made_by: "harin",
    remark: "same remark",
  },
];

interface Props {
  setModalFlag: React.Dispatch<React.SetStateAction<boolean>>;
  onSelect?: (newValue: AtsRfqMstEdge) => void;
}

const RfqDetails = ({ setModalFlag, onSelect }: Props) => {
  const { Purchase } = useAssetTableJson();

  const [rfqs, setRfqs] = useState<AtsRfqMstEdge[]>([]);

  const { customer_id } = useUrlParams();

  const { state } = useContext(AppContext);

  const [hasNextPage, setHasNextPage] = useState<boolean>(true);
  const [endCursor, setEndCursor] = useState<string | null>(null);

  const [dates, setDates] = useState<formDates>({
    startDate: "",
    endDate: "",
  });

  const [GetRFQ, { loading, data: rfqData, fetchMore }] = useLazyQuery<
    GetRfqsResult,
    GetRfqsVars
  >(GetRfqs);



  const dynamicHeaders: TableHeaderProps[] =
    Purchase.PurchaseOrder.RfqDetails.map((header) => ({
      headerName: header.headerName,
      field: header.field,
      headerAlign: header.headerAlign as GridAlignment,
      align: header.align as GridAlignment,
      flex: header.flex,
      hideable: header.hideable,
      renderCell: (params) => {
        if (header.field === "actions") {
          const foundRFQ = rfqs.find((rfq) => rfq.node.id === params.row.rfqId);
          return (
            <>
              <button
                className="mrf-details__tableblock--choose"
                onClick={() => {
                  if (onSelect && foundRFQ) {
                    onSelect(foundRFQ);
                    setModalFlag(false);
                  }
                }}
              >
                Choose RFQ <img src={Assign} alt="" />{" "}
              </button>
            </>
          );
        }
        return params.value;
      },
    }));

  const columns: GridColDef[] = [...dynamicHeaders];
  const rows: GridValidRowModel[] = useMemo(() => {
    return rfqs.map((data, index) => {
      const approx_net_value: number = 0;
      return {
        id: index + 1, // Ensure the ID is unique
        rfq_no: data.node.rfqNo, // Leave empty or provide placeholder values
        rfq_date: toStandardDate(data.node.rfqDate),
        subsidiary: EMPTY_STRING,
        total_item_accepted: data.node.itemQty,
        total_quantity_accepted: data.node.totalQty,
        approx_net_value,
        rfq_authorized_by: EMPTY_STRING,
        remarks: data.node.remarks,
        rfqId: data.node.id,
        actions: "",
      };
    });
  }, [rfqs]);

  useEffect(() => {
    if (customer_id && state.ActiveFinYr) {
      GetRFQ({
        variables: {
          
          after: null,
          customerId: customer_id,
          finYrId: state.ActiveFinYr.id,
          first: ROWS_PER_PAGE,
          name: EMPTY_STRING,
          end_date: toIsoDate(dates.endDate),
          start_date: toIsoDate(dates.startDate),
          queryType: RfqQueryType.ALL,
          orderBy: {
            direction: OrderDirection.ASC,
            field: AtsRfqMstOrderField.RFQ_DATE,
          },
        },
      });
    }
  }, [customer_id, state.ActiveFinYr, GetRFQ, dates.endDate, dates.startDate]);

  useEffect(() => {
    if (state.ActiveFinYr) {
      setDates({
        endDate: toIsoDate(state.ActiveFinYr.finEndDate),
        startDate: toIsoDate(state.ActiveFinYr.finStDate),
      });
    }
  }, [state.ActiveFinYr]);
  useEffect(() => {
    if (rfqData && !loading) {
      const newData = rfqData.GetRfqs.edges;

      if (endCursor) {
        const updatedNewData = newData.map((newCompany) => {
          const filteredStudent = rfqs.find(
            (employee) => employee.node.id === newCompany.node.id
          );
          if (filteredStudent) {
            return {
              ...newCompany,
              node: {
                ...newCompany.node,
              },
            };
          }
          return newCompany;
        });
        setRfqs(updatedNewData);
      } else {
        setRfqs(newData);
      }
      setEndCursor(rfqData.GetRfqs.pageInfo.endCursor);
    }
    // eslint-disable-next-line
  }, [rfqData, loading]);

  useEffect(() => {
    const scrollTable = document.getElementsByClassName(
      "MuiDataGrid-virtualScroller"
    )[0] as Element;
    const handleScroll = (e: Event) => {
      const target = e.target as HTMLDivElement;
      const scrollTop = target.scrollTop;
      const scrollHeight = target.scrollHeight;
      const clientHeight = target.clientHeight;
      if (scrollTop + clientHeight >= getModifiedScrollHeight(scrollHeight)) {
        if (hasNextPage && !loading) {
          fetchMore({
            variables: {
              first: FETCH_MORE_DATA,
              after: endCursor,
            },
            updateQuery: (prevResult, { fetchMoreResult }) => {
              if (!fetchMoreResult) return prevResult;

              const newEdges = fetchMoreResult.GetRfqs.edges;
              const pageInfo = fetchMoreResult.GetRfqs.pageInfo;
              setEndCursor(pageInfo.endCursor);
              setHasNextPage(pageInfo.hasNextPage);

              const duplicateCheck = prevResult.GetRfqs.edges.filter(
                ({ node: { id } }) =>
                  newEdges.findIndex(
                    ({ node: { id: newId } }) => newId === id
                  ) !== -1
              );

              if (duplicateCheck.length > 0) return prevResult;

              return {
                GetRfqs: {
                  edges: [...rfqs, ...newEdges],
                  pageInfo,
                  totalCount: rfqData ? rfqData.GetRfqs.totalCount! : 0,
                },
              };
            },
          });
        }
      }
    };
    if (scrollTable && rows.length)
      scrollTable.addEventListener("scroll", handleScroll);

    return () => {
      if (scrollTable) scrollTable.removeEventListener("scroll", handleScroll);
    };
  }, [rows]);

  return (
    <>
      <div className="mrf-details">
        <div className="mrf-details__title">
          <Title>RFQ Details</Title>
          <img src={Close} alt="" onClick={() => setModalFlag(false)} />
        </div>
        <div className="mrf-details__tableblock">
          <StyledDatagrid
            columns={columns}
            rows={rows}
            rowHeight={TABLE_ROW_HEIGHT}
            disableRowSelectionOnClick
            hideFooter
          />
        </div>
        <Button mode="cancel" onClick={() => setModalFlag(false)} />
      </div>
    </>
  );
};

export default RfqDetails;
